import { getRouteData, type RouteName } from '@shared/constants/routes'
import { resolveProductRouteName } from '@shared/utils/productRoute'
import { type PageRouteData } from '@/features/shared/utils/setPageProps'
import {
  type GeneratedRouteEntry,
  routeAccessManifest,
} from '@shared/constants/routeAccessManifest'
import { type ParamMap } from '@next-types/routes'

type RouteDiscriminator = (match: RegExpExecArray) => RouteName

const staticEntriesByUrl = new Map<string, GeneratedRouteEntry>()
const dynamicEntries: GeneratedRouteEntry[] = []

for (const entry of routeAccessManifest) {
  if (entry.urlPattern.includes('[')) {
    dynamicEntries.push(entry)
  } else {
    staticEntriesByUrl.set(entry.urlPattern, entry)
  }
}

// some pages choose their RouteName from a parameter's VALUE, not its shape. every manifest
// entry with >1 routeName MUST have a discriminator here, and every discriminator
// key MUST be a real multi name urlPattern. this is exported for testing purposes.
export const discriminators = new Map<keyof ParamMap, RouteDiscriminator>([
  ['/shop/products/[id]', (match) => resolveProductRouteName(match[1])],
])

const normalizePathname = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

const resolveRouteName = (
  entry: GeneratedRouteEntry,
  match?: RegExpExecArray,
): RouteName | undefined => {
  const discriminator = discriminators.get(entry.urlPattern)

  if (entry.routeNames.length > 1) {
    return match ? discriminator?.(match) : undefined
  }
  return (match && discriminator?.(match)) ?? entry.routeNames[0]
}

const toRouteData = (
  entry: GeneratedRouteEntry,
  match?: RegExpExecArray,
): PageRouteData | undefined => {
  const routeName = resolveRouteName(entry, match)
  return routeName ? getRouteData(routeName) : undefined
}

export const matchRouteByPath = (pathname: string): PageRouteData | undefined => {
  const normalized = normalizePathname(pathname)

  const staticEntry = staticEntriesByUrl.get(normalized)
  if (staticEntry) return toRouteData(staticEntry)

  for (const entry of dynamicEntries) {
    const match = entry.regex.exec(normalized)
    if (match) return toRouteData(entry, match)
  }

  return undefined
}
